import { useI18n } from '@/lib/i18n';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ArtworkCard from '@/components/ArtworkCard';
import { ArrowRight, Instagram } from 'lucide-react';

const Index = () => {
  const { t, lang } = useI18n();

  const { data: featuredArtworks = [] } = useQuery({
    queryKey: ['featured-artworks'],
    queryFn: async () => {
      const { data } = await supabase.from('artworks').select('*').eq('featured', true).limit(4);
      return data || [];
    },
  });

  const { data: latestArtworks = [] } = useQuery({
    queryKey: ['latest-artworks'],
    queryFn: async () => {
      const { data } = await supabase.from('artworks').select('*').order('created_at', { ascending: false }).limit(8);
      return data || [];
    },
  });

  const displayArtworks = featuredArtworks.length > 0 ? featuredArtworks : latestArtworks;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-background/20 backdrop-blur-[2px]" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        
        <div className="container mx-auto px-4 pt-20 relative z-10 text-center">
          <img src="/logo.png" alt="Melody Art Gallery" className="h-32 md:h-44 w-auto mx-auto mb-6 drop-shadow-lg" />
          <div className="mb-8">
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-gradient leading-tight mb-2">
              Melody Art Gallery
            </h1>
            <p className="font-sans text-sm md:text-lg text-muted-foreground uppercase tracking-[0.3em] font-light">
              Premium Artistic Expression
            </p>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://www.instagram.com/dreaminggirl4ever" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="gradient-accent text-primary-foreground shadow-glow hover:opacity-90 transition-opacity">
                <Instagram className={`h-4 w-4 ${lang === 'ar' ? 'ml-2' : 'mr-2'}`} />
                @dreaminggirl4ever
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Featured/Latest Artworks */}
      {displayArtworks.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                {t('featuredTitle')}
              </h2>
              <p className="text-muted-foreground text-sm">{t('featuredSubtitle')}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {displayArtworks.map(artwork => (
                <ArtworkCard
                  key={artwork.id}
                  id={artwork.id}
                  title={artwork.title}
                  titleAr={artwork.title_ar}
                  price={artwork.price}
                  imageUrl={artwork.image_url}
                  category={artwork.category}
                />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/gallery">
                <Button variant="outline" size="lg" className="border-primary/30 text-primary hover:bg-primary/10">
                  {t('gallery')} →
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Index;
